import type { RuleBoxCategoryFilter, RuleBoxFacets } from "@loot-os/api-contract";
import {
  categoryKeyLabel,
  removeCategoryFilter,
  setCategoryFilter,
} from "@loot-os/api-contract/shopifyRuleBoxForm";

import { ChoicePicker } from "./ChoicePicker";
import { fieldValue } from "./fieldValue";
import { ScopeGate } from "./ScopeGate";

/**
 * The category attribute filters: the attributes Shopify's product categories give (Color,
 * Material, Age group…), one picker per attribute the merchant added, and a select to add another.
 * Reading them needs `read_metaobjects`, which the merchant grants here.
 */
export function CategoryFilters({
  facets,
  filters,
  pending,
  onAllow,
  onChange,
}: {
  facets: RuleBoxFacets;
  filters: RuleBoxCategoryFilter[];
  pending: boolean;
  onAllow: () => void;
  onChange: (next: RuleBoxCategoryFilter[]) => void;
}) {
  if (!facets.categoryValuesNamed && filters.length === 0) {
    return (
      <ScopeGate pending={pending} onAllow={onAllow}>
        Filter by the attributes your product categories give, like color or material. Loot Boxes
        needs access to your category attributes to read them.
      </ScopeGate>
    );
  }
  const taken = new Set(filters.map((f) => f.key));
  const addable = facets.categoryAttributes.filter((a) => !taken.has(a.key));
  return (
    <s-stack gap="base">
      {facets.categoryAttributes.length === 0 && filters.length === 0 && (
        <s-text color="subdued">
          None of your products have category attributes yet. Give a product a category in Shopify
          and fill in its attributes to filter by them.
        </s-text>
      )}
      {filters.map((filter) => {
        const facet = facets.categoryAttributes.find((a) => a.key === filter.key);
        const name = facet?.label ?? categoryKeyLabel(filter.key);
        return (
          <s-stack key={filter.key} gap="small-200">
            <ChoicePicker
              label={name}
              details={`Only products whose ${name.toLowerCase()} is one of these.`}
              placeholder={`Search ${name.toLowerCase()} values`}
              options={facet?.values ?? []}
              chosen={filter.values}
              freeText={false}
              noun="values"
              missingLabel="Removed value"
              onChange={(next) => onChange(setCategoryFilter(filters, filter.key, next))}
            />
            <s-stack direction="inline">
              <s-button
                variant="tertiary"
                tone="critical"
                onClick={() => onChange(removeCategoryFilter(filters, filter.key))}
              >
                {`Remove ${name} filter`}
              </s-button>
            </s-stack>
          </s-stack>
        );
      })}
      {addable.length > 0 && (
        <s-select
          // Remounted whenever the filters change, so it shows its placeholder again: the native
          // select keeps the last pick, and picking it again after a removal would not fire.
          key={filters.map((f) => f.key).join("\u0000")}
          label="Filter by a category attribute"
          value=""
          onChange={(e) => {
            const key = fieldValue(e);
            if (key) onChange(setCategoryFilter(filters, key, []));
          }}
        >
          <s-option value="">Choose an attribute</s-option>
          {addable.map((attribute) => (
            <s-option key={attribute.key} value={attribute.key}>
              {attribute.label}
            </s-option>
          ))}
        </s-select>
      )}
    </s-stack>
  );
}
